import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { CiterightContext } from '../CiterightContextProvider';
import { Button } from 'js/design_system/Button';
import { Menu } from 'js/design_system/Menu';
import { MenuItem } from 'js/design_system/MenuItem';
import { PopoverMode } from 'js/design_system/Popover';
import { SavedReferencesModal } from '../SavedReferencesModal';
import { useCiterightCitations } from '../hooks/useCiterightCitations';
import { exportCitationsAsText, ExportFormat } from '../utils/exportCitations';
import { EXPORT_FORMATS } from '../constants/exportFormats';
import { trackClick } from 'js/utils/trackClick';

const SplitButtonGroup = styled.div`
  display: flex;
  gap: 0;

  & > *:first-child button,
  & > button:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  & > button:last-child,
  & > div:last-child > button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: 1px solid var(--stroke-light);
    padding-left: var(--spacing-xs-8);
    padding-right: var(--spacing-xs-8);
    min-width: 32px;
  }
`;

interface SaveReferencesButtonProps {
  fullSize: boolean;
}

export const SaveReferencesButton: React.FC<SaveReferencesButtonProps> = ({
  fullSize,
}) => {
  const context = useContext(CiterightContext);

  const {
    generateBibtexForSavedReferences,
    generateRisForSavedReferences,
    generateCitationsForSavedReferences,
    isLoading: isExporting,
  } = useCiterightCitations();

  const handleExportCitations = async (format: ExportFormat) => {
    if (!context?.savedReferences.length) return;

    const sourceCounts: Record<string, number> = {};
    for (const ref of context.savedReferences) {
      const src = ref.source || 'unknown';
      sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    }

    trackClick({
      source: 'citeright.references_export',
      entityId: context?.reportId ?? undefined,
      extra: {
        format: format.toLowerCase(),
        reference_count: context.savedReferences.length,
        ...sourceCounts,
      },
    });

    if (format === 'BibTeX') {
      await generateBibtexForSavedReferences(context.savedReferences);
    } else if (format === 'RIS') {
      await generateRisForSavedReferences(context.savedReferences);
    } else {
      const citations = await generateCitationsForSavedReferences(
        context.savedReferences,
      );
      exportCitationsAsText(citations, format);
    }
  };

  return (
    <SplitButtonGroup>
      <SavedReferencesModal iconOnly={!fullSize} />
      <Menu
        mode={PopoverMode.Click}
        placement="bottom-end"
        closeOnClick={true}
        render={() =>
          (context?.savedReferences.length ?? 0) > 0 ? (
            <div>
              {EXPORT_FORMATS.map(({ format, label }) => (
                <MenuItem
                  key={format}
                  onClick={
                    isExporting || !context?.savedReferences.length
                      ? undefined
                      : () => handleExportCitations(format)
                  }
                >
                  {label}
                </MenuItem>
              ))}
            </div>
          ) : (
            <MenuItem>No references yet — add some to get started</MenuItem>
          )
        }
      >
        <Button size="sm" icon="arrow_drop_down" />
      </Menu>
    </SplitButtonGroup>
  );
};
